import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  UserNotificationActionSubmitKind,
  NotificationHubContract,
} from '@automatick/shared-ts';
import {
  type UserNotificationActionPayload,
  type UserNotificationResponse,
} from '@automatick/shared-ng';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import {
  type UserNotificationActionOutcome,
  UserNotificationActionExecutor,
} from './user-notification-action-executor';
import { UserNotificationBrowserCardStore } from './user-notification-browser-card-store';
import { type UserNotificationPreferencePatch } from './user-notification-contract-validator';
import {
  type UserNotificationFeedState,
  UserNotificationFeedRuntime,
} from './user-notification-feed-runtime';
import {
  NotificationLifecyclePhase,
  type UserNotificationCenterState,
  type UserNotificationQueryOptions,
} from './user-notification.models';
import {
  type UserNotificationSecondaryState,
  UserNotificationSecondaryRuntime,
} from './user-notification-secondary-runtime';
import { buildIdleRealtimeConnectionHealth } from '../shared/realtime/realtime-connection-health';

export {
  NotificationLifecyclePhase,
  type NotificationLifecyclePhase as NotificationLifecyclePhaseType,
  type UserNotificationCenterState,
  type UserNotificationQueryOptions,
} from './user-notification.models';

@Injectable({ providedIn: 'root' })
export class UserNotificationService implements OnDestroy {
  private readonly feed = inject(UserNotificationFeedRuntime);
  private readonly secondary = inject(UserNotificationSecondaryRuntime);
  private readonly actions = inject(UserNotificationActionExecutor);
  private readonly destroy$ = new Subject<void>();
  private readonly browserCards = new UserNotificationBrowserCardStore(
    () =>
      typeof document === 'undefined' || document.visibilityState === 'visible',
  );
  private readonly stateSubject =
    new BehaviorSubject<UserNotificationCenterState>({
      notifications: [],
      deliveryHistory: [],
      selectedDeliveryNotificationId: '',
      preferences: null,
      unreadCount: 0,
      actionRequiredCount: 0,
      isLoading: false,
      isDeliveryHistoryLoading: false,
      isPreferencesSaving: false,
      notificationPhase: NotificationLifecyclePhase.Idle,
      deliveryPhase: NotificationLifecyclePhase.Idle,
      preferencesPhase: NotificationLifecyclePhase.Idle,
      activeActionNotificationIds: [],
      isRealtimeConnected: false,
      realtimeHealth: buildIdleRealtimeConnectionHealth(
        NotificationHubContract.ReceiveMethod,
      ),
      notificationMessage: '',
      actionMessage: '',
      deliveryMessage: '',
      preferencesMessage: '',
      realtimeMessage: '',
      errorMessage: '',
    });
  private bootstrapStarted = false;
  private destroyed = false;
  private activeWorkspaceId: string | null = null;

  readonly state$ = this.stateSubject.asObservable();
  readonly browserCards$ = this.browserCards.cards$;

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.browserCards.clear();
  }

  bootstrap(): void {
    if (this.bootstrapStarted) {
      return;
    }
    this.bootstrapStarted = true;

    this.feed.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => this.applyFeedState(state));
    this.feed.received$
      .pipe(takeUntil(this.destroy$))
      .subscribe((notification) => this.browserCards.show(notification));
    this.secondary.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => this.applySecondaryState(state));
    this.actions.outcomes$
      .pipe(takeUntil(this.destroy$))
      .subscribe((outcome) => this.applyActionOutcome(outcome));
    this.actions.leaseChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshActiveActionIds());

    this.feed.bootstrap();
  }

  refresh(): void {
    this.feed.refresh();
  }

  load(options?: UserNotificationQueryOptions): void {
    this.feed.load(options);
  }

  markRead(notification: UserNotificationResponse): void {
    this.submitAction(notification, UserNotificationActionSubmitKind.MarkRead);
  }

  dismiss(notification: UserNotificationResponse): void {
    this.submitAction(notification, UserNotificationActionSubmitKind.Dismiss);
  }

  complete(notification: UserNotificationResponse): void {
    this.submitAction(notification, UserNotificationActionSubmitKind.Complete);
  }

  submitAction(
    notification: UserNotificationResponse,
    actionKind: UserNotificationActionSubmitKind,
    actionId?: string | null,
  ): void {
    const workspaceId = this.activeWorkspaceId;
    if (workspaceId) {
      this.patch({ actionMessage: '' });
      this.actions.submitAction(
        workspaceId,
        notification,
        actionKind,
        actionId,
      );
    }
  }

  submitResponseAction(
    notification: UserNotificationResponse,
    action: UserNotificationActionPayload,
  ): void {
    const workspaceId = this.activeWorkspaceId;
    if (!workspaceId) {
      this.patch({
        actionMessage: 'Notification response could not be submitted.',
      });
      return;
    }
    this.patch({ actionMessage: '' });
    this.actions.submitResponseAction(workspaceId, notification, action);
  }

  setActiveConversationSession(sessionId: string | null | undefined): void {
    this.browserCards.setActiveConversationSession(sessionId);
  }

  dismissBrowserCard(notificationId: string | null | undefined): void {
    this.browserCards.dismiss(notificationId);
  }

  loadPreferences(): void {
    this.secondary.loadPreferences();
  }

  updatePreferences(patch: UserNotificationPreferencePatch): void {
    this.secondary.updatePreferences(patch);
  }

  loadDeliveryHistory(notification: UserNotificationResponse): void {
    this.secondary.loadDeliveryHistory(notification);
  }

  closeDeliveryHistory(): void {
    this.secondary.closeDeliveryHistory();
  }

  private applyFeedState(state: UserNotificationFeedState): void {
    const workspaceChanged = this.activeWorkspaceId !== state.workspaceId;
    this.activeWorkspaceId = state.workspaceId;
    if (workspaceChanged) {
      this.browserCards.clear();
      this.secondary.activateWorkspace(state.workspaceId);
    }
    this.patch({
      notifications: state.notifications,
      unreadCount: state.unreadCount,
      actionRequiredCount: state.actionRequiredCount,
      isLoading: state.isLoading,
      notificationPhase: state.phase,
      notificationMessage: state.message,
      isRealtimeConnected: state.isRealtimeConnected,
      realtimeHealth: state.realtimeHealth,
      realtimeMessage: state.realtimeMessage,
      ...(workspaceChanged ? { actionMessage: '' } : {}),
    });
    this.secondary.closeDeliveryIfMissing(state.notifications);
    this.refreshActiveActionIds();
  }

  private applySecondaryState(state: UserNotificationSecondaryState): void {
    if (state.workspaceId !== this.activeWorkspaceId) {
      return;
    }
    this.patch({
      deliveryHistory: state.deliveryHistory,
      selectedDeliveryNotificationId: state.selectedDeliveryNotificationId,
      preferences: state.preferences,
      isDeliveryHistoryLoading: state.isDeliveryHistoryLoading,
      isPreferencesSaving: state.isPreferencesSaving,
      deliveryPhase: state.deliveryPhase,
      preferencesPhase: state.preferencesPhase,
      deliveryMessage: state.deliveryMessage,
      preferencesMessage: state.preferencesMessage,
    });
  }

  private applyActionOutcome(outcome: UserNotificationActionOutcome): void {
    if (outcome.workspaceId !== this.activeWorkspaceId) {
      return;
    }
    if (outcome.notification) {
      this.feed.upsert(outcome.notification);
    }
    this.patch({ actionMessage: outcome.message ?? '' });
  }

  private refreshActiveActionIds(): void {
    this.patch({
      activeActionNotificationIds: this.activeWorkspaceId
        ? this.actions.activeNotificationIds(this.activeWorkspaceId)
        : [],
    });
  }

  private patch(patch: Partial<UserNotificationCenterState>): void {
    if (this.destroyed) {
      return;
    }
    const nextState = { ...this.stateSubject.value, ...patch };
    this.stateSubject.next({
      ...nextState,
      errorMessage:
        nextState.notificationMessage || nextState.actionMessage || '',
    });
  }
}
